/**
 * wayai contacts — a contact's customer record in one of the organization's bases (#5083 RK2 A3).
 *
 * Usage:
 *   wayai contacts link-customer <contact_id> --base <base_id> --record <record_id> [--org <org_id>] [--json]
 *   wayai contacts unlink-customer <contact_id> --base <base_id> [--org <org_id>] [--json]
 *
 * Both go to `/api/contact-book/contacts/:contactId/customer`, which only a person signed in through
 * the browser (`wayai login`) reaches — an API token is refused (owner decision 125 Q4), and the
 * message here says so. A refusal's message is the route's, sanitized.
 */

import { parseArgs } from 'node:util';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { envTokenOverride, identifyTokenType } from '../lib/token-store.js';
import { BASE_ID_RULE, isValidBaseId } from '../lib/base-id.js';
import { UUID_RE, wantsHelp } from '../lib/utils.js';

const USAGE = `
wayai contacts — a contact's customer record in one of the organization's bases

Usage:
  wayai contacts link-customer <contact_id> --base <base_id> --record <record_id> [--org <org_id>] [--json]
  wayai contacts unlink-customer <contact_id> --base <base_id> [--org <org_id>] [--json]

link-customer    Make the record the contact's customer record: a conversation with the
                 contact (its WhatsApp phone, Instagram id or app account) then serves that
                 customer through a customer-scoped toolset. The record is one of the base's
                 customer record type (x-customer-scope role root), holding no external key
                 or the contact id under another source. A record keyed under another
                 external_id is refused: re-keying it would orphan its owned records.
unlink-customer  Unlink the contact's customer record in the base. The record keeps the
                 contact id under the source wayai_contact_unlinked, so its owned records stay
                 its own, and link-customer on the same record links it again.

The organization is --org, else this repo's (wayai-ws/wayai.yaml). You must be an
organization admin. These commands need a browser sign-in (\`wayai login\`): an API
token (\`wayai login --token\`, or WAYAI_TOKEN) is refused.
`.trim();

const SUBCOMMANDS = ['link-customer', 'unlink-customer'] as const;
type Subcommand = (typeof SUBCOMMANDS)[number];

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

function parse(args: string[]) {
  try {
    return parseArgs({
      args,
      options: {
        org: { type: 'string' },
        base: { type: 'string' },
        record: { type: 'string' },
        json: { type: 'boolean' },
      },
      allowPositionals: true,
      strict: true,
    });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

/** The org from `--org`, else the repo's binding; refused when neither names one. */
function resolveOrgId(flag: string | undefined): string {
  if (flag !== undefined) {
    if (!UUID_RE.test(flag)) throw expected(`Invalid --org ${JSON.stringify(flag)}: expected an organization id (a UUID).`);
    return flag;
  }
  const bound = readRepoConfigUnlessBlocked()?.organization_id;
  if (bound) return bound;
  throw expected('No organization: pass --org <org_id>, or run this inside a workspace scoped with `wayai init`.');
}

/**
 * A refusal as a message naming the next step: for an API token's 403, the browser sign-in (these
 * routes take a person only); else the route's own message.
 */
function failure(err: unknown, accessToken: string): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 403 && identifyTokenType(accessToken) === 'way_token') {
    const override = envTokenOverride()
      ? ' WAYAI_TOKEN is set in this shell and is sent instead of your sign-in: unset it first.'
      : '';
    return expected(
      "A contact's customer record is linked from a browser sign-in only, and this login is an API token. " +
        `Run \`wayai login\` (without --token).${override}`,
    );
  }
  if (err.status >= 400 && err.status < 500) return expected(sanitizeTerminalText(extractApiMessage(err)));
  return err;
}

export async function contactsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { values, positionals } = parse(args);
  const [sub, contactId, ...extra] = positionals;
  if (!SUBCOMMANDS.includes(sub as Subcommand)) throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  if (contactId === undefined || extra.length > 0) throw usageError(`${sub} takes one <contact_id>.`);
  if (!UUID_RE.test(contactId)) throw usageError(`Invalid <contact_id> ${JSON.stringify(contactId)}: expected a contact id (a UUID).`);
  if (!values.base) throw usageError(`${sub} needs --base <base_id>.`);
  if (!isValidBaseId(values.base)) throw usageError(`Invalid --base ${JSON.stringify(values.base)}: a base id is ${BASE_ID_RULE}.`);
  if (sub === 'link-customer' && !values.record) throw usageError('link-customer needs --record <record_id>.');
  if (sub === 'unlink-customer' && values.record !== undefined) throw usageError('--record applies only to link-customer.');
  const orgId = resolveOrgId(values.org);

  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing.
  const api = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });

  let customer;
  try {
    ({ customer } = sub === 'link-customer'
      ? await api.linkContactCustomer(orgId, contactId, { base_id: values.base, record_id: values.record! })
      : await api.unlinkContactCustomer(orgId, contactId, values.base));
  } catch (err) {
    throw failure(err, accessToken);
  }

  if (values.json) {
    console.log(JSON.stringify(customer, null, 2));
    return;
  }
  const where = `base ${sanitizeTerminalText(customer.base_id)} (${sanitizeTerminalText(customer.record_type)})`;
  console.log(sub === 'link-customer'
    ? `Contact ${customer.contact_id} is linked to customer record ${sanitizeTerminalText(customer.record_id)} in ${where}.`
    : `Contact ${customer.contact_id} is unlinked from customer record ${sanitizeTerminalText(customer.record_id)} in ${where}; ` +
      `the record keeps the contact id under ${customer.external_source}.`);
}
